import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, CheckCircle2, XCircle, Sparkles, ThumbsUp, ThumbsDown, Lightbulb, MessageSquare, Film, TrendingUp, HelpCircle, Send } from "lucide-react";

const CreatorDashboard = () => {
    const [profile, setProfile] = useState(null);
    const [briefs, setBriefs] = useState([]);
    const [active, setActive] = useState([]);
    const [earnings, setEarnings] = useState(null);
    const [insights, setInsights] = useState(null);
    const [feedback, setFeedback] = useState([]);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState("");
    const [sendingReply, setSendingReply] = useState(false);

    useEffect(() => {
        api.get("/creator/profile").then((r) => setProfile(r.data));
        api.get("/creator/briefs").then((r) => setBriefs(r.data));
        api.get("/creator/active-work").then((r) => setActive(r.data));
        api.get("/creator/earnings").then((r) => setEarnings(r.data));
        api.get("/creator/insights").then((r) => setInsights(r.data));
        api.get("/creator/feedback").then((r) => setFeedback(r.data)).catch(() => {});
        api.get("/creator/messages").then((r) => setMessages(r.data)).catch(() => {});
    }, []);

    const sendReply = async () => {
        if (!reply.trim()) return;
        setSendingReply(true);
        try {
            const r = await api.post("/creator/messages", { body: reply.trim() });
            setMessages((m) => [...m, r.data]);
            setReply("");
            toast.success("Reply sent to the vetting team");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send reply");
        } finally { setSendingReply(false); }
    };

    const vetting = profile?.vetting_status;
    const renderVettingBanner = () => {
        if (vetting === "approved") return (
            <div className="yc-card p-6 bg-yc-blue text-white flex items-center gap-4"><CheckCircle2 className="w-8 h-8" /><div><div className="yc-overline text-white">Approved</div><div className="font-heading text-2xl font-bold">You&apos;re in the network.</div></div></div>
        );
        if (vetting === "rejected") return (
            <div className="yc-card p-6 bg-yc-pink text-white flex items-center gap-4"><XCircle className="w-8 h-8" /><div><div className="yc-overline text-white">Update needed</div><div className="font-heading text-2xl font-bold">Your application needs work.</div>{profile?.vetting_notes && <p className="text-sm mt-1">{profile.vetting_notes}</p>}</div></div>
        );
        if (vetting === "more_info") return (
            <div className="yc-card p-6 bg-yc-pink text-white flex items-center gap-4" data-testid="banner-more-info"><HelpCircle className="w-8 h-8" /><div><div className="yc-overline text-white">Action needed</div><div className="font-heading text-2xl font-bold">We need a little more information.</div><div className="text-sm mt-1 text-white/90">Check the messages below and reply — your application stays in the queue.</div></div></div>
        );
        if (vetting === "pending") return (
            <div className="yc-card p-6 flex items-center gap-4"><Clock className="w-8 h-8 text-yc-blue" /><div><div className="yc-overline">In review</div><div className="font-heading text-2xl font-bold">Your profile is under review.</div><div className="text-sm text-yc-ink/70 mt-1">We aim to vet within 48 hours.</div></div></div>
        );
        return (
            <div className="yc-card p-6 bg-yc-cream flex items-center gap-4"><Sparkles className="w-8 h-8 text-yc-pink" /><div><div className="yc-overline">Get started</div><div className="font-heading text-2xl font-bold">Complete your profile to apply.</div><Link to="/creator/onboarding" className="yc-btn-primary mt-3 text-sm py-2 px-3" data-testid="dash-complete-profile">Complete profile</Link></div></div>
        );
    };

    const showThread = messages.length > 0 || vetting === "more_info";

    return (
        <DashboardShell role="creator" title={`Hello ${profile?.name || "Creator"}`} subtitle="Profile, briefs, active work, earnings">
            {profile?.is_demo && (
                <div className="yc-card p-4 bg-yc-pink text-white flex items-center gap-3" data-testid="creator-demo-banner">
                    <Sparkles className="w-5 h-5 shrink-0" />
                    <div className="text-sm font-semibold">
                        You&apos;re exploring a creator demo — briefs, work and earnings here are fictional. Want to join the real network?{" "}
                        <a href="/creators" className="underline">Apply here</a>.
                    </div>
                </div>
            )}
            {renderVettingBanner()}

            {showThread && (
                <div className="yc-card p-6" data-testid="creator-messages-card">
                    <div className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-yc-blue" /><h3 className="font-heading text-xl font-bold">Vetting team messages</h3></div>
                    <div className="mt-4 space-y-3 max-h-72 overflow-y-auto pr-1">
                        {messages.map((m) => (
                            <div key={m.message_id} className={`max-w-[85%] ${m.sender === "creator" ? "ml-auto" : ""}`}>
                                <div className={`border-2 border-yc-ink rounded-2xl px-4 py-2.5 text-sm ${m.sender === "creator" ? "bg-yc-blue text-white" : "bg-yc-cream"}`}>
                                    {m.body}
                                </div>
                                <div className={`text-[10px] text-yc-ink/50 mt-1 ${m.sender === "creator" ? "text-right" : ""}`}>
                                    {m.sender === "creator" ? "You" : "Curated Chaos team"} · {new Date(m.created_at).toLocaleString()}
                                </div>
                            </div>
                        ))}
                        {messages.length === 0 && <div className="text-center text-yc-ink/50 text-sm py-4">No messages yet.</div>}
                    </div>
                    <div className="flex gap-2 mt-4">
                        <input
                            data-testid="creator-reply-input"
                            className="yc-input flex-1"
                            placeholder="Reply to the vetting team…"
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") sendReply(); }}
                        />
                        <button data-testid="creator-reply-send" disabled={sendingReply || !reply.trim()} onClick={sendReply} className="yc-btn-primary px-4 disabled:opacity-50">
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="yc-card-sm p-6">
                    <div className="yc-overline">Quality</div>
                    <div className="font-heading text-5xl font-black tracking-tighter mt-2">{Math.round(profile?.quality_score || 0)}</div>
                </div>
                <div className="yc-card-sm p-6">
                    <div className="yc-overline">Reliability</div>
                    <div className="font-heading text-5xl font-black tracking-tighter mt-2">{Math.round(profile?.reliability_score || 0)}</div>
                </div>
                <div className="yc-card-sm p-6">
                    <div className="yc-overline">Earnings</div>
                    <div className="font-heading text-5xl font-black tracking-tighter mt-2">£{(earnings?.total_released || 0).toFixed(0)}</div>
                    <div className="text-xs text-yc-ink/60 mt-1">From {earnings?.count || 0} placements</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="yc-card p-6">
                    <h3 className="font-heading text-xl font-bold">Available briefs</h3>
                    {vetting !== "approved" && <p className="text-sm text-yc-ink/70 mt-2">Briefs appear once you&apos;re approved.</p>}
                    <div className="mt-4 divide-y divide-yc-ink/10">
                        {briefs.map((b) => (
                            <div key={b.campaign_id} className="py-3" data-testid={`brief-${b.campaign_id}`}>
                                <div className="font-semibold">{b.title}</div>
                                <div className="text-xs text-yc-ink/60">{b.videos_count ? `${b.videos_count} video${b.videos_count === 1 ? "" : "s"}` : b.package_id ? `${b.package_id} package` : ""}</div>
                            </div>
                        ))}
                        {briefs.length === 0 && vetting === "approved" && <div className="py-6 text-center text-yc-ink/60 text-sm">No open briefs right now.</div>}
                    </div>
                </div>
                <div className="yc-card p-6">
                    <h3 className="font-heading text-xl font-bold">Active work</h3>
                    <div className="mt-4 divide-y divide-yc-ink/10">
                        {active.map((p) => (
                            <Link key={p.placement_id} to={`/placement/${p.placement_id}`} className="py-3 flex items-center justify-between hover:bg-yc-cream/40 px-2 rounded-xl" data-testid={`active-${p.placement_id}`}>
                                <div>
                                    <div className="font-semibold">Placement {p.placement_id.slice(0, 6)}…</div>
                                    <div className="text-xs text-yc-ink/60">Status: {p.status}</div>
                                </div>
                                <span className="yc-badge bg-yc-cream">Open →</span>
                            </Link>
                        ))}
                        {active.length === 0 && <div className="py-6 text-center text-yc-ink/60 text-sm">No active placements.</div>}
                    </div>
                </div>
            </div>

            {/* Brand feedback received */}
            {feedback.length > 0 && (
                <div className="yc-card p-6" data-testid="creator-feedback-received">
                    <h3 className="font-heading text-xl font-bold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-yc-pink" /> Feedback from brands</h3>
                    <p className="text-xs text-yc-ink/60 mt-1">Structured, constructive feedback from campaigns you've delivered — use it to boost your profile.</p>
                    <div className="mt-4 space-y-4">
                        {feedback.map((f) => (
                            <div key={f.placement_id} className="border-2 border-yc-ink rounded-2xl p-4 bg-white" data-testid={`feedback-item-${f.placement_id}`}>
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="font-semibold text-sm">{f.campaign_title || "Campaign"}{f.brand_name ? ` · ${f.brand_name}` : ""}</div>
                                    {f.brand_rating > 0 && <span className="yc-badge bg-yc-blue text-white">{f.brand_rating}/5</span>}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-sm">
                                    <div><div className="yc-overline text-[10px] flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> Went well</div><p className="mt-1">{f.went_well}</p></div>
                                    <div><div className="yc-overline text-[10px] flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Ways to improve</div><p className="mt-1">{f.ways_to_improve}</p></div>
                                    <div><div className="yc-overline text-[10px] flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Could change</div><p className="mt-1">{f.what_could_change}</p></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Insights breakdown */}
            <div className="yc-card p-6" data-testid="creator-breakdown">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Film className="w-5 h-5 text-yc-blue" /> Your production breakdown</h3>
                    <span className="text-xs text-yc-ink/60">{insights?.videos_produced || 0} videos produced · {insights?.completed_placements || 0} placements complete</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
                    {[
                        { l: "Videos", v: insights?.videos_produced || 0, tid: "metric-videos" },
                        { l: "Reach", v: insights?.performance?.reach || 0, tid: "metric-reach" },
                        { l: "Engagement", v: insights?.performance?.engagement || 0, tid: "metric-engagement" },
                        { l: "Likes", v: insights?.performance?.likes || 0, tid: "metric-likes" },
                        { l: "Avg rating", v: `${insights?.performance?.avg_rating || 0}/5`, tid: "metric-rating" },
                    ].map((m) => (
                        <div key={m.l} className="border-2 border-yc-ink rounded-2xl p-4 bg-yc-cream" data-testid={m.tid}>
                            <div className="yc-overline">{m.l}</div>
                            <div className="font-heading font-black text-3xl tracking-tighter mt-1">{typeof m.v === "number" ? m.v.toLocaleString() : m.v}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="yc-card p-6" data-testid="what-worked">
                    <div className="flex items-center gap-2"><ThumbsUp className="w-5 h-5 text-yc-blue" /><h3 className="font-heading text-lg font-bold">What worked</h3></div>
                    <ul className="mt-4 space-y-2 text-sm">
                        {(insights?.what_worked || []).map((x, i) => (
                            <li key={`ww-${i}`} className="flex gap-2 leading-relaxed"><CheckCircle2 className="w-4 h-4 text-yc-blue flex-shrink-0 mt-0.5" /><span>{x}</span></li>
                        ))}
                        {(!insights || insights.what_worked.length === 0) && <li className="text-yc-ink/60">Highlights appear once placements complete.</li>}
                    </ul>
                </div>
                <div className="yc-card p-6" data-testid="what-didnt">
                    <div className="flex items-center gap-2"><ThumbsDown className="w-5 h-5 text-yc-pink" /><h3 className="font-heading text-lg font-bold">What didn&apos;t</h3></div>
                    <ul className="mt-4 space-y-2 text-sm">
                        {(insights?.what_didnt || []).map((x, i) => (
                            <li key={`wd-${i}`} className="flex gap-2 leading-relaxed"><XCircle className="w-4 h-4 text-yc-pink flex-shrink-0 mt-0.5" /><span>{x}</span></li>
                        ))}
                        {(!insights || insights.what_didnt.length === 0) && <li className="text-yc-ink/60">Nothing flagged — keep it up.</li>}
                    </ul>
                </div>
                <div className="yc-card p-6" data-testid="improvements">
                    <div className="flex items-center gap-2"><Lightbulb className="w-5 h-5 text-yc-ink" /><h3 className="font-heading text-lg font-bold">Improvements</h3></div>
                    <ul className="mt-4 space-y-2 text-sm">
                        {(insights?.improvements || []).map((x, i) => (
                            <li key={`imp-${i}`} className="flex gap-2 leading-relaxed"><TrendingUp className="w-4 h-4 text-yc-ink flex-shrink-0 mt-0.5" /><span>{x}</span></li>
                        ))}
                        {(!insights || insights.improvements.length === 0) && <li className="text-yc-ink/60">You&apos;re ahead of the curve.</li>}
                    </ul>
                </div>
            </div>

            <div className="yc-card p-6" data-testid="client-feedback">
                <div className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-yc-pink" /><h3 className="font-heading text-xl font-bold">Client feedback</h3></div>
                <div className="mt-4 space-y-3">
                    {(insights?.feedback || []).map((f) => (
                        <div key={f.placement_id} className="border-2 border-yc-ink rounded-2xl p-4 bg-yc-cream">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="font-heading font-bold">{f.brand_name} · {f.campaign_title}</div>
                                <span className="yc-badge bg-white">{f.rating}/5</span>
                            </div>
                            <p className="text-sm mt-2 leading-relaxed">&ldquo;{f.notes}&rdquo;</p>
                        </div>
                    ))}
                    {(!insights || insights.feedback.length === 0) && (
                        <div className="text-yc-ink/60 text-sm py-6 text-center">No feedback yet. Brands rate and write notes when they release payment on a completed placement.</div>
                    )}
                </div>
            </div>
        </DashboardShell>
    );
};

export default CreatorDashboard;
